import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, File, Plus, Minus } from 'lucide-react';

interface FileDiff {
  filename: string;
  oldFilename?: string;
  hunks: DiffHunk[];
  additions: number;
  deletions: number;
  isNew: boolean;
  isDeleted: boolean;
  isRenamed: boolean;
}

interface DiffHunk {
  header: string;
  oldStart: number;
  oldLines: number;
  newStart: number;
  newLines: number;
  lines: DiffLine[];
}

interface DiffLine {
  type: 'context' | 'addition' | 'deletion' | 'header';
  content: string;
  oldLineNumber?: number;
  newLineNumber?: number;
}

const parseDiff = (diffText: string): FileDiff[] => {
  const files: FileDiff[] = [];
  const lines = diffText.split('\n');
  let currentFile: FileDiff | null = null;
  let currentHunk: DiffHunk | null = null;
  let oldLineNum = 0;
  let newLineNum = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // New file diff header
    if (line.startsWith('diff --git')) {
      if (currentFile) {
        if (currentHunk) currentFile.hunks.push(currentHunk);
        files.push(currentFile);
      }

      const match = line.match(/diff --git a\/(.+) b\/(.+)/);
      const filename = match ? match[2] : 'unknown';
      const oldFilename = match ? match[1] : undefined;

      currentFile = {
        filename,
        oldFilename: oldFilename !== filename ? oldFilename : undefined,
        hunks: [],
        additions: 0,
        deletions: 0,
        isNew: false,
        isDeleted: false,
        isRenamed: oldFilename !== filename,
      };
      currentHunk = null;
      continue;
    }

    if (!currentFile) continue;

    // File mode indicators
    if (line.startsWith('new file mode')) {
      currentFile.isNew = true;
      continue;
    }
    if (line.startsWith('deleted file mode')) {
      currentFile.isDeleted = true;
      continue;
    }

    // Skip index, --- and +++ lines
    if (line.startsWith('index ') || line.startsWith('--- ') || line.startsWith('+++ ')) {
      continue;
    }

    // Hunk header
    if (line.startsWith('@@')) {
      if (currentHunk) currentFile.hunks.push(currentHunk);

      const match = line.match(/@@ -(\d+),?(\d*) \+(\d+),?(\d*) @@(.*)/);
      if (match) {
        oldLineNum = parseInt(match[1], 10);
        newLineNum = parseInt(match[3], 10);
        currentHunk = {
          header: line,
          oldStart: oldLineNum,
          oldLines: match[2] ? parseInt(match[2], 10) : 1,
          newStart: newLineNum,
          newLines: match[4] ? parseInt(match[4], 10) : 1,
          lines: [{
            type: 'header',
            content: match[5]?.trim() || '',
          }],
        };
      }
      continue;
    }

    if (!currentHunk) continue;

    // Diff lines
    if (line.startsWith('+')) {
      currentHunk.lines.push({
        type: 'addition',
        content: line.slice(1),
        newLineNumber: newLineNum++,
      });
      currentFile.additions++;
    } else if (line.startsWith('-')) {
      currentHunk.lines.push({
        type: 'deletion',
        content: line.slice(1),
        oldLineNumber: oldLineNum++,
      });
      currentFile.deletions++;
    } else if (line.startsWith(' ') || line === '') {
      currentHunk.lines.push({
        type: 'context',
        content: line.slice(1) || '',
        oldLineNumber: oldLineNum++,
        newLineNumber: newLineNum++,
      });
    }
  }

  // Push last file and hunk
  if (currentFile) {
    if (currentHunk) currentFile.hunks.push(currentHunk);
    files.push(currentFile);
  }

  return files;
};

interface FileHeaderProps {
  file: FileDiff;
  isExpanded: boolean;
  onToggle: () => void;
}

const FileHeader: React.FC<FileHeaderProps> = ({ file, isExpanded, onToggle }) => {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-3 px-4 py-3 bg-ui-background-tertiary hover:bg-ui-background-tertiary transition-colors"
    >
      {isExpanded ? (
        <ChevronDown className="w-4 h-4 text-ui-text-tertiary shrink-0" />
      ) : (
        <ChevronRight className="w-4 h-4 text-ui-text-tertiary shrink-0" />
      )}
      <File className="w-4 h-4 text-ui-text-tertiary shrink-0" />
      <span className="flex-1 text-left font-mono text-sm text-ui-text-primary truncate">
        {file.isRenamed && file.oldFilename ? (
          <>
            <span className="text-ui-text-tertiary">{file.oldFilename}</span>
            <span className="text-ui-text-tertiary mx-2">→</span>
            {file.filename}
          </>
        ) : (
          file.filename
        )}
      </span>
      <div className="flex items-center gap-2 text-xs shrink-0">
        {file.isNew && (
          <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
            New
          </span>
        )}
        {file.isDeleted && (
          <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded">
            Deleted
          </span>
        )}
        {file.additions > 0 && (
          <span className="text-green-600 dark:text-green-400 font-medium">
            +{file.additions}
          </span>
        )}
        {file.deletions > 0 && (
          <span className="text-red-600 dark:text-red-400 font-medium">
            -{file.deletions}
          </span>
        )}
      </div>
    </button>
  );
};

interface DiffContentProps {
  file: FileDiff;
}

const DiffContent: React.FC<DiffContentProps> = ({ file }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm font-mono border-collapse">
        <tbody>
          {file.hunks.map((hunk, hunkIndex) => (
            <React.Fragment key={hunkIndex}>
              {/* Hunk header */}
              <tr className="bg-blue-50 dark:bg-blue-900/20">
                <td
                  colSpan={3}
                  className="px-4 py-1 text-blue-600 dark:text-blue-400 text-xs"
                >
                  {hunk.header}
                </td>
              </tr>
              {/* Diff lines */}
              {hunk.lines.filter(l => l.type !== 'header').map((line, lineIndex) => (
                <tr
                  key={`${hunkIndex}-${lineIndex}`}
                  className={
                    line.type === 'addition'
                      ? 'bg-green-50 dark:bg-green-900/20'
                      : line.type === 'deletion'
                      ? 'bg-red-50 dark:bg-red-900/20'
                      : ''
                  }
                >
                  {/* Old line number */}
                  <td className="w-12 px-2 py-0 text-right text-ui-text-tertiary select-none border-r border-ui-border-primary text-xs">
                    {line.type !== 'addition' ? line.oldLineNumber : ''}
                  </td>
                  {/* New line number */}
                  <td className="w-12 px-2 py-0 text-right text-ui-text-tertiary select-none border-r border-ui-border-primary text-xs">
                    {line.type !== 'deletion' ? line.newLineNumber : ''}
                  </td>
                  {/* Content */}
                  <td className="px-4 py-0 whitespace-pre">
                    <span
                      className={
                        line.type === 'addition'
                          ? 'text-green-700 dark:text-green-400'
                          : line.type === 'deletion'
                          ? 'text-red-700 dark:text-red-400'
                          : 'text-ui-text-secondary'
                      }
                    >
                      {line.type === 'addition' && (
                        <span className="inline-block w-4 text-center">+</span>
                      )}
                      {line.type === 'deletion' && (
                        <span className="inline-block w-4 text-center">-</span>
                      )}
                      {line.type === 'context' && (
                        <span className="inline-block w-4 text-center"> </span>
                      )}
                      {line.content}
                    </span>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface DiffViewerProps {
  diff: string;
}

const DiffViewer: React.FC<DiffViewerProps> = ({ diff }) => {
  const files = useMemo(() => parseDiff(diff), [diff]);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(() => 
    new Set(files.map(f => f.filename))
  );

  const toggleFile = (filename: string) => {
    setExpandedFiles(prev => {
      const next = new Set(prev);
      if (next.has(filename)) {
        next.delete(filename);
      } else {
        next.add(filename);
      }
      return next;
    });
  };

  const totalAdditions = files.reduce((sum, f) => sum + f.additions, 0);
  const totalDeletions = files.reduce((sum, f) => sum + f.deletions, 0);

  if (files.length === 0) {
    return (
      <div className="text-center py-8 text-ui-text-tertiary">
        No changes to display
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm">
        <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
          <span className="text-ui-text-secondary">
            {files.length} {files.length === 1 ? 'file' : 'files'} changed
          </span>
          <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
            <Plus className="w-4 h-4" />
            <span>
              <span className="sm:hidden">{totalAdditions}</span>
              <span className="hidden sm:inline">{totalAdditions} additions</span>
            </span>
          </div>
          <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
            <Minus className="w-4 h-4" />
            <span>
              <span className="sm:hidden">{totalDeletions}</span>
              <span className="hidden sm:inline">{totalDeletions} deletions</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 sm:ml-auto">
          <button
            onClick={() => setExpandedFiles(new Set(files.map(f => f.filename)))}
            className="text-xs text-brand-primary hover:text-brand-primaryDark dark:text-brand-primaryDarkMode"
          >
            Expand all
          </button>
          <button
            onClick={() => setExpandedFiles(new Set())}
            className="text-xs text-brand-primary hover:text-brand-primaryDark dark:text-brand-primaryDarkMode"
          >
            Collapse all
          </button>
        </div>
      </div>

      {/* Files */}
      <div className="border border-ui-border-primary rounded-lg overflow-hidden">
        {files.map((file, index) => (
          <div
            key={file.filename}
            className={index > 0 ? 'border-t border-ui-border-primary' : ''}
          >
            <FileHeader
              file={file}
              isExpanded={expandedFiles.has(file.filename)}
              onToggle={() => toggleFile(file.filename)}
            />
            {expandedFiles.has(file.filename) && <DiffContent file={file} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DiffViewer;
